import { useCallback } from 'react';
import {
  useActiveDocumentInfoContext,
  useActiveDocumentRefresh,
} from '../../../shared/hooks/ActiveDocumentProvider';

export interface MappingModelDataRefresh {
  modelDataStale: boolean;
  handleRefreshModelData: () => void;
}

/**
 * Page-level Refresh action for model-derived mapping data. ModelChanged events
 * only park a pending model version; this adopts it (which re-keys the
 * version-scoped readiness snapshot, so the catalog re-check follows on its
 * own) and falls back to an explicit readiness refresh only when there is no
 * newer version to adopt — one click, one coherent reload.
 */
export function useMappingModelDataRefresh(input: {
  refreshReadiness: () => void;
}): MappingModelDataRefresh {
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const { modelDataStale, pendingModelVersion, refreshModelData } = useActiveDocumentRefresh();
  const { refreshReadiness } = input;

  const handleRefreshModelData = useCallback(() => {
    const versionWillAdvance =
      pendingModelVersion !== null && pendingModelVersion !== activeDocumentInfo.modelVersion;
    refreshModelData();
    if (!versionWillAdvance) {
      refreshReadiness();
    }
  }, [activeDocumentInfo.modelVersion, pendingModelVersion, refreshModelData, refreshReadiness]);

  return { modelDataStale, handleRefreshModelData };
}
